import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, OnDestroy } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { UsuariosContextService } from "../../context/usuarios-context.service";
import { SessionContextService } from 'projects/packcert-web/src/app/context/session-context.service';
import { UsuariosService } from "../../services/usuarios.service";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';

@Component({
    selector: 'app-auditores',
    templateUrl: './auditores.component.html'
})
export class AuditoresComponent implements OnInit, OnDestroy {

    private readonly _DEFAULT_ITEMS_PER_PAGE = 10;

    private _filters;
    public filtersVisible = false;
    public filtersForm: UntypedFormGroup;

    public toCloseSpinner = 0;

    public auditores;
    public denominaciones;
    public campanyas;

    public paisId;
    public auditorId;
    public applicationUserId;
    public auditor;

    public mensajes = new Array<Message>();
    public showError: boolean;
    public showWarning: boolean;

    public deleteModal = false;
    public newEditModal = false;
    public sendInvitationModal = false;

    public refreshCheckbox_habilitado = false;
    public refreshCheckbox_permisoEscritura = false;

    public disabledDenominaciones: Subject<boolean> = new Subject();
    public disabledCampanyas: Subject<boolean> = new Subject();

    public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
    public currentPage = 0;
    public totalItems: number;
    public readonly visiblePages = 3;

    public orderBy: string;
    public columnId: number;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly _router: Router,
        private _fb: UntypedFormBuilder,
        private readonly _spinnerService: SpinnerService,
        private readonly _usuariosService: UsuariosService,
        private readonly _dataProvider: DataProvider,
        private readonly _usuariosContext: UsuariosContextService,
        private readonly utilsService: UtilsService,
        private readonly _sessionContext: SessionContextService,
        private readonly _translate: TranslateService
    ) { }

    ngOnInit() {
        this.filtersForm = this._fb.group({
            des: [''],
            entidadControl: [''],
            fechaInicio: [''],
            fechaFin: [''],
            dateType: ['false'],
            habilitado: [''],
            permisoEscritura: ['true']
        });

        this._usuariosContext.auditoresFilters = null;

        this.getAuditores();

        this.filtersForm.get('fechaInicio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
        this.filtersForm.get('fechaFin').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());

    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get entidades() {
        return this._dataProvider.entidadesControl;
    }

    public get role() {
        return this._sessionContext.sessionUser.role;
    }

    public get claims() {
        return this._sessionContext.sessionUser.lsClaimTypes;
    }

    public get defaultLanguage() {
      return this._translate.getDefaultLang();
    }

    public get isSmallScreen(){
      return this._sessionContext.isSmallScreen();
    }

    private clearDatesValidators() {
        if (this.filtersForm.get('fechaFin').errors && this.filtersForm.get('fechaFin').value) {
            this.filtersForm.get('fechaFin').clearValidators();
            this.filtersForm.get('fechaFin').updateValueAndValidity();
        }
        if (this.filtersForm.get('fechaInicio').errors && this.filtersForm.get('fechaInicio').value) {
            this.filtersForm.get('fechaInicio').clearValidators();
            this.filtersForm.get('fechaInicio').updateValueAndValidity();
        }
    }

    public validDates() {
        if (this.filtersForm.get('fechaInicio').value && this.filtersForm.get('fechaFin').value) {
            if (this.checkDates(this.filtersForm.get('fechaInicio').value, this.filtersForm.get('fechaFin').value)) {
                this.clearDatesValidators();
            } else {
                this.filtersForm.get('fechaInicio').setErrors(['Invalid date']);
                this.filtersForm.get('fechaFin').setErrors(['Invalid date']);
            }
            if (this.filtersForm.get('fechaInicio').invalid || this.filtersForm.get('fechaFin').invalid) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.fecha.error.finAnteriorInicio'});
            } else {
                this.showError = false;
                this.mensajes = [];
            }
        } else {
            this.clearDatesValidators();
            this.showError = false;
            this.mensajes = [];
        }
    }

    private checkDates(inicio, fin) {
        const inicioD = new Date(inicio);
        const finD = new Date(fin);
        return inicioD <= finD;
    }

    public pageChange(page) {
        this._usuariosContext.auditoresFilters.numPagina = this.currentPage = page - 1;
        this.getAuditores();
    }

    public itemsPerPageChange(items) {
        this._usuariosContext.auditoresFilters.numPagina = this.currentPage = 0;
        this._usuariosContext.auditoresFilters.tamPagina = this.itemsPerPage = items;
        this.getAuditores();
    }

    public checkValuesForm(): boolean {
        return (this.filtersForm.get('entidadControl').value ||
            this.filtersForm.get('des').value ||
            !this.filtersForm.get('habilitado').pristine ||
            !this.filtersForm.get('permisoEscritura').pristine ||
            this.filtersForm.get('fechaInicio').value ||
            this.filtersForm.get('fechaFin').value) ? true : false;
    }

    public resetForm() {
        this.filtersForm.reset();
        this.filtersForm.get('dateType').patchValue('false');
        this.refreshCheckbox_habilitado = true;
        this.refreshCheckbox_permisoEscritura = true;
    }

    public pristineCheckbox(idForm:string, pristine:boolean){
      const form = this.filtersForm.get(idForm);
      pristine ? form.markAsPristine() : form.markAsDirty();
    }

    public filtrar() {
        this.validDates();
        
        if (this.filtersForm.valid) {
            this.applyFilters();
        }
    }

    private applyFilters() {
        this.currentPage = 0;
        this._usuariosContext.auditoresFilters = {
            entidadControlId: this.filtersForm.get('entidadControl').value ? this.filtersForm.get('entidadControl').value.id : null,
            desAuditor: this.filtersForm.get('des').value ? this.filtersForm.get('des').value : '',
            cargaRegistroEnUso: true,
            activo: this.filtersForm.get('habilitado').value ? this.filtersForm.get('habilitado').value : '',
            roleName: this.filtersForm.get('permisoEscritura').value == 'true' ? 'Auditor' : this.filtersForm.get('permisoEscritura').value == 'false' ? 'Supervisor' : null,
            fechaInicio: this.filtersForm.get('fechaInicio').value ?
                this.utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaInicio').value) : '',
            fechaFin: this.filtersForm.get('fechaFin').value ?
                this.utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaFin').value) : '',
            fechaActualizacion: this.filtersForm.get('dateType').value,
            numPagina: this.currentPage,
            tamPagina: this.itemsPerPage
        };
        this.getAuditores();
    }

    public getAuditores() {
        if (this._usuariosContext.auditoresFilters) {
            this._filters = this._usuariosContext.auditoresFilters;
        } else {
            this.applyFilters();
        }
        this._spinnerService.showSpinner();
        this._usuariosService.getAuditores(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.totalItems = resp.paginacionMeta.totalCount;
            this.auditores = resp.lista;
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        });
    }

    public create() {
        this.auditorId = null;
        this.newEditModal = true;
    }

    public update(auditor) {
        this.auditorId = auditor.id;
        this.applicationUserId = auditor.applicationUserId;
        this.newEditModal = true;
    }

    public block(auditor) {
        let aud = {
            userName: auditor.userName,
            activo: !auditor.applicationUser.activo
        };
        this._spinnerService.showSpinner();
        this._usuariosService.blockAuditor(aud).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            if (auditor.applicationUser.activo) {
                this.pushMessage('global.success.itemBlocked');
            } else {
                this.pushMessage('global.success.itemUnBlocked');
            }
            this.getAuditores();
        });
    }

    public sendInvitation() {
        this.auditor = null;
        this.sendInvitationModal = true;
    }

    public close() {
        this.deleteModal = false;
        this.newEditModal = false;
        this.sendInvitationModal = false;
    }

    public refresh(value) {
        this.newEditModal = false;
        this.sendInvitationModal = false;
        if (value) {
            this.getAuditores();
        }
    }

    private checkCloseSpinner() {
        this.toCloseSpinner -= 1;
        if (this.toCloseSpinner === 0) {
            this._spinnerService.hideSpinner();
        }
    }

    public pushMessage(mensaje) {
        this.mensajes = [];
        this.showWarning = true;
        this.mensajes.push({ class: 'success', message: mensaje });
    }

    public askDelete(auditor) {
        this.auditor = auditor;
        this.deleteModal = true;
    }

    public delete() {
        this._spinnerService.showSpinner();
        this._usuariosService.deleteAuditor(this.auditor.id).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then(() => {
            this.close();
            this.pushMessage('global.success.itemDeleted');
            this.getAuditores();
        });
    }

    public getIcon(prop: string) {
      let iconClass = 'fa fa-sort';
      if (this._filters && this._filters.orderItems && this._filters.orderItems[0].orderProperty.indexOf(prop) != -1) {
          iconClass = this._filters.orderItems[0].orderByDescending === true ? 'fa fa-sort-down' : 'fa fa-sort-up';
      }
      return iconClass;
    }

    public toOrderBy(columnFilter, idColumn) {
      this.columnId = idColumn;
      if (this.auditores && this.auditores.length > 0) {
        if (this._usuariosContext.auditoresFilters) {
          this._filters = this._usuariosContext.auditoresFilters;
          if (!this.orderBy || this.orderBy.includes('DESC')) {
            this.orderBy = 'ASC' + idColumn;
            this._filters.orderItems = [
              {
                orderProperty: columnFilter,
                orderByDescending: false
              }
            ];
          } else if (this.orderBy.includes('ASC')) {
            this.orderBy = 'DESC' + idColumn;
            this._filters.orderItems = [
              {
                orderProperty: columnFilter,
                orderByDescending: true
              }
            ];
          }
          this.getAuditores();
        }
      }
    }

    public openDocHelp() {
      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._usuariosService.getDocHelp('Users_Auditors.pdf').toPromise().finally(() => {
          this.checkCloseSpinner();
      }).then((resp) => {
          localStorage.setItem('base64', resp.base64Archivo);
          const url = this._router.serializeUrl(this._router.createUrlTree(['viewPdf']));
          window.open(url, '_blank');
      });
    }

    public trackByUserName(index: number, auditor: { userName: string }): string {
        return auditor.userName;
    }
}
